import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ExternalLink,
  Filter,
  Library,
  Loader,
  Maximize2,
  Pencil,
  Plus,
  Search,
  Trash2,
  UserCog,
} from 'lucide-react';
import { useTheme } from '../../../components/common/Theme';
import PermissionsAssignmentModal from '../common/Permissions/PermissionsAssignmentModal';
import { CatalogApp, CatalogAppFormData, CatalogAvailability } from './catalogTypes';
import { getCatalogIcon } from './catalogIcons';
import RegisterCatalogAppModal from './RegisterCatalogAppModal';
import CatalogAppPopup from './CatalogAppPopup';
import CatalogConfirmDialog from './CatalogConfirmDialog';
import { catalogService } from './catalogService';
import { logCatalog } from './catalogLogger';

const CATALOG_MODULE_ID = 'e8c4a1b2-7d3f-4a91-8e5c-2b6f9d0a4c17';

const AVAILABILITY_FILTERS: { value: 'all' | CatalogAvailability; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'copilot', label: 'Copilot' },
  { value: 'standalone', label: 'Standalone' },
  { value: 'both', label: 'Both' },
];

function statusClasses(status: CatalogApp['status']): string {
  switch (status) {
    case 'active':
      return 'text-green-400 bg-green-400/10 border-green-400/20';
    case 'maintenance':
      return 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20';
    default:
      return 'text-gray-400 bg-gray-400/10 border-gray-400/20';
  }
}

function availabilityLabel(value: CatalogAvailability): string {
  if (value === 'copilot') return 'Copilot';
  if (value === 'standalone') return 'Standalone';
  return 'Both';
}

function toFormData(app: CatalogApp): CatalogAppFormData {
  return {
    name: app.name,
    description: app.description,
    icon: app.icon,
    iconColor: app.iconColor || 'indigo',
    iconUrl: app.iconUrl || '',
    dns: app.dns,
    url: app.url,
    status: app.status,
    appCode: app.appCode,
    ownerId: app.ownerId,
    availability: app.availability,
    copilotIds: Array.isArray(app.copilotIds) ? app.copilotIds : [],
  };
}

interface CatalogCardProps {
  app: CatalogApp;
  isLight: boolean;
  onLive: (app: CatalogApp) => void;
  onPopup: (app: CatalogApp) => void;
  onEdit: (app: CatalogApp) => void;
  onDelete: (app: CatalogApp) => void;
  onPermissions: (app: CatalogApp) => void;
}

const CatalogCard: React.FC<CatalogCardProps> = ({ app, isLight, onLive, onPopup, onEdit, onDelete, onPermissions }) => {
  const Icon = getCatalogIcon(app.icon);
  const copilotCount = Array.isArray(app.copilotIds) ? app.copilotIds.length : 0;
  const iconBtn = isLight
    ? 'text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100'
    : 'text-gray-400 hover:text-white hover:bg-neutral-800';

  return (
    <div
      className={`rounded-lg p-5 border flex flex-col transition-all group ${
        isLight
          ? 'bg-white border-neutral-200 hover:border-indigo-400/40 shadow-sm'
          : 'bg-neutral-900 border-neutral-800 hover:border-indigo-500/30'
      }`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-3 min-w-0">
          <div className={`p-2.5 rounded-lg shrink-0 overflow-hidden ${isLight ? 'bg-neutral-100 border border-neutral-200' : 'bg-neutral-800 border border-neutral-700'}`}>
            {app.iconUrl ? (
              <img src={app.iconUrl} alt="" className="w-5 h-5 object-cover rounded" />
            ) : (
              <Icon size={20} className={isLight ? 'text-neutral-700' : 'text-white'} />
            )}
          </div>
          <div className="min-w-0">
            <h3 className={`font-semibold text-base leading-tight truncate ${isLight ? 'text-neutral-900' : 'text-white'}`}>{app.name}</h3>
            <p className={`text-xs mt-0.5 ${isLight ? 'text-neutral-500' : 'text-gray-400'}`}>{app.appCode}</p>
          </div>
        </div>
        <span className={`shrink-0 px-2 py-1 rounded-md text-xs font-medium border capitalize ${statusClasses(app.status)}`}>
          {app.status}
        </span>
      </div>

      <p className={`text-xs leading-relaxed line-clamp-2 mb-3 ${isLight ? 'text-neutral-600' : 'text-gray-300'}`}>
        {app.description || 'No description provided.'}
      </p>

      <div className="flex flex-wrap gap-1 mb-3">
        <span className={`px-2 py-0.5 rounded-full text-xs ${isLight ? 'bg-neutral-100 text-neutral-600' : 'bg-neutral-800 text-gray-300'}`}>
          {availabilityLabel(app.availability)}
        </span>
        {copilotCount > 0 && (
          <span className={`px-2 py-0.5 rounded-full text-xs ${isLight ? 'bg-indigo-50 text-indigo-700' : 'bg-indigo-500/15 text-indigo-300'}`}>
            {copilotCount} copilot{copilotCount === 1 ? '' : 's'}
          </span>
        )}
      </div>

      <div className={`text-xs space-y-1 mb-3 ${isLight ? 'text-neutral-500' : 'text-gray-400'}`}>
        <p className="truncate">
          DNS <span className={isLight ? 'text-neutral-700' : 'text-gray-300'}>{app.dns}</span>
        </p>
        <p className={`truncate font-mono ${isLight ? 'text-neutral-400' : 'text-gray-500'}`}>{app.ownerId}</p>
      </div>

      <div className={`mt-auto flex items-center space-x-2 pt-3 border-t ${isLight ? 'border-neutral-200' : 'border-neutral-800'}`}>
        <button
          type="button"
          onClick={() => onLive(app)}
          disabled={app.status === 'inactive'}
          className="flex items-center px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md text-xs transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ExternalLink size={12} className="mr-1" />
          Live
        </button>
        <button
          type="button"
          onClick={() => onPopup(app)}
          disabled={app.status === 'inactive'}
          title="Open in popup"
          className={`p-1.5 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${iconBtn}`}
        >
          <Maximize2 size={14} />
        </button>
        <button
          type="button"
          className="text-indigo-400 hover:text-indigo-300 transition-colors p-1.5"
          title="Manage Permissions & Access"
          onClick={() => onPermissions(app)}
        >
          <UserCog className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => onEdit(app)}
          title="Edit"
          className={`p-1.5 rounded-md transition-colors ${iconBtn}`}
        >
          <Pencil size={14} />
        </button>
        <button
          type="button"
          onClick={() => onDelete(app)}
          title="Delete"
          className={`p-1.5 rounded-md transition-colors ${
            isLight ? 'text-neutral-400 hover:text-red-500 hover:bg-red-50' : 'text-gray-400 hover:text-red-400 hover:bg-neutral-800'
          }`}
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
};

const CatalogListing: React.FC = () => {
  const { theme } = useTheme();
  const isLight = theme === 'white';
  const [apps, setApps] = useState<CatalogApp[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [availabilityFilter, setAvailabilityFilter] = useState<'all' | CatalogAvailability>('all');
  const [showRegister, setShowRegister] = useState(false);
  const [editingApp, setEditingApp] = useState<CatalogApp | null>(null);
  const [popupApp, setPopupApp] = useState<CatalogApp | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CatalogApp | null>(null);
  const [permissionApp, setPermissionApp] = useState<CatalogApp | null>(null);

  const loadApps = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const items = await catalogService.list();
      setApps(items);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load catalog';
      setError(message);
      setApps([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadApps();
  }, [loadApps]);

  const existingCodes = useMemo(() => {
    const current = editingApp?.appCode.toUpperCase();
    return apps.map((a) => a.appCode.toUpperCase()).filter((code) => code !== current);
  }, [apps, editingApp]);

  const editingForm = useMemo(() => (editingApp ? toFormData(editingApp) : null), [editingApp]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return apps.filter((app) => {
      const matchesSearch =
        !q ||
        app.name.toLowerCase().includes(q) ||
        app.description.toLowerCase().includes(q) ||
        app.appCode.toLowerCase().includes(q) ||
        app.dns.toLowerCase().includes(q);
      const matchesAvailability = availabilityFilter === 'all' || app.availability === availabilityFilter;
      return matchesSearch && matchesAvailability;
    });
  }, [apps, searchQuery, availabilityFilter]);

  const activeCount = apps.filter((a) => a.status === 'active').length;

  const handleLive = (app: CatalogApp) => {
    logCatalog('app', 'open_live', { id: app.id, url: app.url });
    window.open(app.url, '_blank', 'noopener,noreferrer');
  };

  const handlePopup = (app: CatalogApp) => {
    logCatalog('app', 'open_popup', { id: app.id, url: app.url });
    setPopupApp(app);
  };

  const closeModal = () => {
    setShowRegister(false);
    setEditingApp(null);
  };

  const handleSave = async (data: CatalogAppFormData) => {
    setError(null);
    try {
      if (editingApp) {
        const updated = await catalogService.update(editingApp.id, data);
        setApps((prev) => prev.map((app) => (app.id === updated.id ? updated : app)));
      } else {
        const created = await catalogService.create(data);
        setApps((prev) => [created, ...prev]);
      }
      closeModal();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save catalog app';
      setError(message);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    setDeleteTarget(null);
    setError(null);
    try {
      await catalogService.remove(target.id);
      setApps((prev) => prev.filter((app) => app.id !== target.id));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete catalog app';
      setError(message);
    }
  };

  return (
    <div>
      <div className={`border-b ${isLight ? 'border-neutral-200 bg-neutral-50' : 'border-neutral-800 bg-neutral-900/50'}`}>
        <div className="max-w-7xl mx-auto px-6 py-5">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h1 className={`text-2xl font-bold flex items-center ${isLight ? 'text-neutral-900' : 'text-white'}`}>
                <Library className="mr-3 text-indigo-400" size={28} />
                Catalog
              </h1>
              <p className={`text-sm mt-1 ${isLight ? 'text-neutral-500' : 'text-gray-400'}`}>
                Registered applications available as copilot, standalone, or both
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setEditingApp(null);
                setShowRegister(true);
              }}
              className="flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors shadow-lg text-sm shrink-0"
            >
              <Plus size={16} className="mr-2" />
              Register app
            </button>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mt-5">
            <div className={`rounded-lg p-3 ${isLight ? 'bg-white border border-neutral-200' : 'bg-neutral-800/50'}`}>
              <div className={`font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>{apps.length}</div>
              <div className={`text-xs ${isLight ? 'text-neutral-500' : 'text-gray-400'}`}>Catalog apps</div>
            </div>
            <div className={`rounded-lg p-3 ${isLight ? 'bg-white border border-neutral-200' : 'bg-neutral-800/50'}`}>
              <div className={`font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>{activeCount}</div>
              <div className={`text-xs ${isLight ? 'text-neutral-500' : 'text-gray-400'}`}>Active</div>
            </div>
            <div className={`rounded-lg p-3 ${isLight ? 'bg-white border border-neutral-200' : 'bg-neutral-800/50'}`}>
              <div className={`font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>
                {apps.filter((a) => a.availability === 'both').length}
              </div>
              <div className={`text-xs ${isLight ? 'text-neutral-500' : 'text-gray-400'}`}>Both copilot & standalone</div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-5">
        {error && (
          <div className={`mb-4 rounded-lg border px-4 py-3 text-sm ${isLight ? 'border-red-200 bg-red-50 text-red-700' : 'border-red-500/20 bg-red-500/10 text-red-300'}`}>
            {error}
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isLight ? 'text-neutral-400' : 'text-gray-400'}`} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search name, code, DNS, or description..."
              className={`w-full pl-10 pr-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm ${
                isLight
                  ? 'bg-white border border-neutral-300 text-neutral-900 placeholder-neutral-400'
                  : 'bg-neutral-800 border border-neutral-700 text-white'
              }`}
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter size={16} className={isLight ? 'text-neutral-400' : 'text-gray-400'} />
            <select
              value={availabilityFilter}
              onChange={(e) => setAvailabilityFilter(e.target.value as 'all' | CatalogAvailability)}
              className={`px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm ${
                isLight ? 'bg-white border border-neutral-300 text-neutral-900' : 'bg-neutral-800 border border-neutral-700 text-white'
              }`}
            >
              {AVAILABILITY_FILTERS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16">
            <Loader size={40} className="mx-auto mb-3 text-indigo-400 animate-spin" />
            <h3 className={`text-lg font-semibold mb-1 ${isLight ? 'text-neutral-700' : 'text-gray-300'}`}>Loading catalog...</h3>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <Library size={40} className={`mx-auto mb-3 ${isLight ? 'text-neutral-300' : 'text-neutral-600'}`} />
            <h3 className={`text-lg font-semibold mb-1 ${isLight ? 'text-neutral-700' : 'text-gray-300'}`}>No catalog apps</h3>
            <p className={`text-sm ${isLight ? 'text-neutral-500' : 'text-gray-500'}`}>
              Try a different search, or register a new application.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((app) => (
              <CatalogCard
                key={app.id}
                app={app}
                isLight={isLight}
                onLive={handleLive}
                onPopup={handlePopup}
                onEdit={(item) => {
                  setEditingApp(item);
                  setShowRegister(true);
                }}
                onDelete={setDeleteTarget}
                onPermissions={setPermissionApp}
              />
            ))}
          </div>
        )}
      </div>

      <RegisterCatalogAppModal
        isOpen={showRegister}
        existingCodes={existingCodes}
        editMode={Boolean(editingApp)}
        initialData={editingForm}
        onClose={closeModal}
        onSave={(data) => {
          void handleSave(data);
        }}
      />

      {popupApp && <CatalogAppPopup app={popupApp} onClose={() => setPopupApp(null)} />}

      {permissionApp && (
        <PermissionsAssignmentModal
          onClose={() => setPermissionApp(null)}
          onSave={() => {}}
          entityType="catalog"
          entityName={permissionApp.name}
          module_id={CATALOG_MODULE_ID}
          object_id={permissionApp.id}
        />
      )}

      {deleteTarget && (
        <CatalogConfirmDialog
          title="Delete this app?"
          message={`Remove “${deleteTarget.name}” (${deleteTarget.appCode}) from the catalog? This cannot be undone.`}
          confirmLabel="Yes, delete"
          danger
          onConfirm={() => {
            void handleConfirmDelete();
          }}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
};

export default CatalogListing;
